import React, { useMemo, useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";

// Mood to genre mapping for intelligent track filtering
const MOOD_TO_GENRES = {
  "Party": ["Dance", "Pop", "Hip-Hop", "Electronic", "House", "Reggaeton", "Latin", "Funk", "Disco"],
  "Chill": ["Jazz", "Soul", "R&B", "Acoustic", "Ambient", "Lo-Fi", "Indie", "Blues", "Folk"],
  "Energetic": ["Rock", "Electronic", "Metal", "Punk", "EDM", "Drum and Bass", "Techno", "Hardstyle"],
  "Romantic": ["R&B", "Soul", "Pop", "Acoustic", "Jazz", "Ballad", "Love Songs", "Soft Rock"]
};

// Filter and score tracks based on mood and genre preferences
const filterTracks = (tracks = [], selectedMoods = [], selectedGenres = []) => {
  if (!tracks || tracks.length === 0) return [];

  const scored = tracks.map(track => {
    let score = 0;
    const trackGenre = track.genrekey?.toLowerCase() || track.genre?.toLowerCase() || '';

    // Score based on mood match
    selectedMoods.forEach(mood => {
      const moodGenres = MOOD_TO_GENRES[mood] || [];
      moodGenres.forEach(genre => {
        if (trackGenre.includes(genre.toLowerCase())) {
          score += 3;
        }
      });
    });

    // Score based on direct genre match (higher weight)
    selectedGenres.forEach(genre => {
      if (trackGenre.includes(genre.toLowerCase())) {
        score += 5;
      }
    });

    return { ...track, score };
  });

  // Return top 20 tracks with score > 0
  return scored
    .filter(t => t.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 20);
};

export const AIRecommendedPanel = ({ selectedMoods = [], selectedGenres = [], tracks = [], clientObj, onTrackClick }) => {
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  // Setup Embla carousel with AutoScroll plugin - matching JukeboxList2 pattern
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      dragFree: true
    },
    [
      AutoScroll({
        playOnInit: true,
        stopOnInteraction: false,
        startDelay: 500,
        speed: 0.5 // Slower than JukeboxList2 for better readability
      })
    ]
  );

  // Filter tracks based on mood and genre
  const filteredTracks = useMemo(() => {
    return filterTracks(tracks, selectedMoods, selectedGenres);
  }, [tracks, selectedMoods, selectedGenres]);

  // Handle auto-scroll state
  useEffect(() => {
    if (!emblaApi) return;

    const onAutoScroll = (emblaApi) => {
      const autoScroll = emblaApi?.plugins()?.autoScroll;
      if (!autoScroll) return;
      setIsAutoScrolling(autoScroll.isPlaying());
    };

    emblaApi.on("autoScroll:play", onAutoScroll);
    emblaApi.on("autoScroll:stop", onAutoScroll);
    emblaApi.on("reInit", onAutoScroll);

    return () => {
      emblaApi.off("autoScroll:play", onAutoScroll);
      emblaApi.off("autoScroll:stop", onAutoScroll);
      emblaApi.off("reInit", onAutoScroll);
    };
  }, [emblaApi]);

  // Handle track click - delegate to parent for animation control
  const handleTrackClick = useCallback((track, index) => {
    if (onTrackClick) {
      onTrackClick(track, index);
    }
  }, [onTrackClick]);

  if (filteredTracks.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-gradient-to-br from-indigo-900/20 via-purple-900/20 to-pink-900/20 rounded-lg px-2 py-4 mb-4">
      <h2 className="text-xs font-bold text-white mb-3 px-2 text-left">
        AI Recommended
      </h2>

      {/* Embla Carousel - Square cards to show full cover art */}
      <div className="embla embla-wide" style={{ overflow: 'hidden', width: '100%', maxWidth: '100%' }}>
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container" style={{ display: 'flex', gap: '12px' }}>
            {filteredTracks.map((track, index) => (
              <div
                key={track.id || index}
                className="embla__slide flex-shrink-0 cursor-pointer transition-transform duration-300 hover:scale-105"
                style={{ flex: '0 0 auto', width: '140px' }}
                onClick={() => handleTrackClick(track, index)}
              >
                {/* Track Card - Square */}
                <div className="relative w-full h-[140px] rounded-lg overflow-hidden shadow-lg">
                  {/* Cover Art Background - 50% opacity */}
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: track.coverart
                        ? `url(${track.coverart})`
                        : 'linear-gradient(135deg, rgb(239, 68, 68), rgb(59, 130, 246))',
                      opacity: 0.5
                    }}
                  />

                  {/* Track Info - Left-aligned at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-2">
                    <h3
                      className="text-xs font-bold text-white line-clamp-1 mb-0.5"
                      style={{
                        textShadow: "0 0 6px rgba(0, 0, 0, 0.9), 1px 1px 2px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px -1px 2px rgba(0, 0, 0, 0.8), -1px 1px 2px rgba(0, 0, 0, 0.8)",
                        lineHeight: "1.4"
                      }}
                    >
                      {track.track}
                    </h3>
                    <p
                      className="text-[10px] text-white line-clamp-1 mb-1"
                      style={{
                        textShadow: "0 0 6px rgba(0, 0, 0, 0.9), 1px 1px 2px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px -1px 2px rgba(0, 0, 0, 0.8), -1px 1px 2px rgba(0, 0, 0, 0.8)"
                      }}
                    >
                      {track.artist}
                    </p>

                    {/* Genre Badge */}
                    {(track.genrekey || track.genre) && (
                      <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold text-white bg-purple-600/80 rounded-full">
                        {track.genrekey || track.genre}
                      </span>
                    )}
                  </div>

                  {/* Score Indicator (for debugging - can be removed) */}
                  {track.score > 0 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-green-500/90 rounded-full flex items-center justify-center">
                      <span className="text-[10px] font-bold text-white" style={{ lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{track.score}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Auto-scroll status */}
      <p className="text-xs text-white/50 text-center mt-2">
        {isAutoScrolling ? 'Auto-scrolling...' : 'Paused'} • {filteredTracks.length} tracks match your vibe
      </p>
    </div>
  );
};

export default AIRecommendedPanel;
